import { Search } from "lucide-react";
import type { ReactNode } from "react";
import clsx from "clsx";
import { PitchPlanBadge } from "@/components/bookings/PitchPlanBadge";
import { guestInitials, type StaySortState } from "@/components/bookings/stayListModel";
import { SortableTableHeader } from "@/components/ui/SortableTableHeader";
import type { Booking } from "@/types/camping";

export function StayListToolbar({
  stats,
  search,
  onSearchChange,
  placeholder,
  searchLabel,
}: {
  stats: ReactNode;
  search: string;
  onSearchChange: (value: string) => void;
  placeholder: string;
  searchLabel: string;
}) {
  return (
    <div className="eh-stay-toolbar">
      <div className="eh-stay-toolbar__stats">{stats}</div>
      <div className="eh-stay-search">
        <Search aria-hidden />
        <input
          type="search"
          className="eh-input"
          value={search}
          placeholder={placeholder}
          aria-label={searchLabel}
          autoComplete="off"
          onChange={(event) => onSearchChange(event.target.value)}
        />
        {search ? (
          <button
            type="button"
            className="eh-stay-search__clear"
            aria-label="Suche zurücksetzen"
            onClick={() => onSearchChange("")}
          >
            ×
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function StayStat({
  children,
  tone = "default",
}: {
  children: ReactNode;
  tone?: "default" | "accent" | "warn" | "danger";
}) {
  return (
    <span className={clsx("eh-stay-stat", tone !== "default" && `eh-stay-stat--${tone}`)}>
      {children}
    </span>
  );
}

export function StayCountLabel({
  shown,
  total,
  singular,
  plural,
  filtering,
}: {
  shown: number;
  total: number;
  singular: string;
  plural: string;
  filtering: boolean;
}) {
  const noun = total === 1 ? singular : plural;
  return (
    <StayStat tone="accent">
      <strong>{filtering ? `${shown} von ${total}` : total}</strong>
      {noun}
    </StayStat>
  );
}

export function StayGuestCell({
  name,
  meta,
  badges,
}: {
  name: string;
  meta: string;
  badges?: ReactNode;
}) {
  return (
    <div className="eh-stay-guest">
      <span className="eh-stay-avatar" aria-hidden>
        {guestInitials(name)}
      </span>
      <span className="min-w-0">
        <span className="eh-stay-guest__name">
          {name}
          {badges}
        </span>
        <span className="eh-stay-guest__meta">{meta}</span>
      </span>
    </div>
  );
}

export function StayPitchCell({
  code,
  category,
  booking,
}: {
  code: string;
  category?: string | null;
  booking: Pick<Booking, "pitch_id" | "pitch_assignment_mode" | "status">;
}) {
  const open = code === "Offen";
  return (
    <div className="eh-stay-pitch">
      <span className={clsx("eh-stay-pitch__code", open && "is-open")}>{code}</span>
      <span className="eh-stay-pitch__meta">
        {category ? <span className="eh-stay-pitch__cat">{category}</span> : null}
        <PitchPlanBadge booking={booking} />
      </span>
    </div>
  );
}

export function StayLines({ primary, secondary }: { primary: ReactNode; secondary?: ReactNode }) {
  return (
    <div>
      <span className="eh-stay-lines__primary">{primary}</span>
      {secondary ? <span className="eh-stay-lines__secondary">{secondary}</span> : null}
    </div>
  );
}

export function StayAccess({
  keyNumber,
  transponderId,
}: {
  keyNumber?: string | null;
  transponderId?: string | null;
}) {
  const key = keyNumber?.trim();
  const transponder = transponderId?.trim();
  if (!key && !transponder) {
    return <span className="text-sm text-label-tertiary">Kein Zugang</span>;
  }
  return (
    <div className="flex flex-col gap-1">
      {key ? (
        <span className="eh-stay-key">
          Schlüssel <strong>{key}</strong>
        </span>
      ) : null}
      {transponder ? (
        <span className="eh-stay-key">
          Transponder <strong>{transponder}</strong>
        </span>
      ) : null}
    </div>
  );
}

export function StaySortHeader({
  label,
  sortKey,
  sort,
  onSort,
  align,
  className,
}: {
  label: string;
  sortKey: string;
  sort: StaySortState | null;
  onSort: (key: string) => void;
  align?: "left" | "right";
  className?: string;
}) {
  return (
    <SortableTableHeader
      label={label}
      active={sort?.key === sortKey}
      direction={sort?.dir ?? "asc"}
      align={align}
      className={className}
      onSort={() => onSort(sortKey)}
    />
  );
}
